import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '@/app/shared/service/api.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { environment } from '@/environments/environment';
import { getHeaders } from '../tramite.service';

@Component({
  selector: 'app-registro-tramite-admin',
  templateUrl: './registro-tramite-admin.component.html',
  styleUrls: ['./registro-tramite-admin.component.scss']
})
export class RegistroTramiteAdminComponent implements OnInit {

  public form: FormGroup;
  public submitted = false;
  public isLoading = false;
  public currentUser: any;
  public userId: any;

  // Opciones para selectores
  public areas: any[] = [];
  public categorias: any[] = [];
  public tiposTramite: any[] = [];
  public dependencias: any[] = [];

  private baseUrl = environment.API_URL;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private apiService: ApiService,
    private toastr: ToastrService,
    private http: HttpClient
  ) {
    this.buildForm();
    this.loadCurrentUser();
  }

  ngOnInit(): void {
    this.loadAreas();
    this.loadCategorias();
    this.loadDependencias();
    this.loadTiposTramite();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      // Información básica del trámite
      nombreTramite: ['', [Validators.required, Validators.minLength(3)]],
      codigoTramite: ['', [Validators.required]],
      tipoTramite: ['', [Validators.required]],
      categoria: ['', [Validators.required]],
      dependencia: ['', [Validators.required]],
      area: ['', [Validators.required]],
      
      // Descripción
      descripcion: ['', [Validators.required, Validators.minLength(10)]],
      fundamentoJuridico: ['', [Validators.required]],
      
      // Modalidades
      modalidadPresencial: [false],
      modalidadEnLinea: [false],
      modalidadConCita: [false],
      
      // Solicitante
      tipoSolicitante: ['', [Validators.required]],
      personaFisica: [false],
      personaMoral: [false],
      
      // Tiempos
      tiempoRespuesta: [0, [Validators.required, Validators.min(0)]],
      tipoTiempoRespuesta: ['Días hábiles', [Validators.required]],
      
      // Vigencia
      vigencia: [0, [Validators.min(0)]],
      tipoVigencia: ['Días naturales'],
      
      // Información de contacto
      nombreContacto: ['', [Validators.required]],
      cargoContacto: [''],
      telefonoContacto: ['', [Validators.required]],
      emailContacto: ['', [Validators.email]],
      
      // Oficina
      nombreOficina: [''],
      direccionOficina: [''],
      telefonoOficina: [''],
      horarioAtencion: [''],
      
      // Costo
      costo: [0, [Validators.min(0)]],
      esGratuito: [true],
      
      // Requisitos
      requisitos: [''],
      
      // Observaciones
      observaciones: [''],
      
      // Configuración
      isVisible: [true],
      isLimited: [false],
      isRequestable: [true]
    });
  }

  private loadCurrentUser(): void {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        this.currentUser = JSON.parse(userStr);
        this.userId = this.currentUser?.id;
      }
    } catch (error) {
      console.error('Error al cargar usuario:', error);
    }
  }

  private loadAreas(): void {
    const headers = getHeaders();
    this.http.get<any[]>(`${this.baseUrl}/catalogs-data/Areas?limit=1000`, { headers })
      .subscribe(
        (data: any) => {
          this.areas = data.list || data.rows || data || [];
        },
        (error) => {
          console.error('Error al cargar áreas:', error);
        }
      );
  }

  private loadCategorias(): void {
    const headers = getHeaders();
    this.http.get<any[]>(`${this.baseUrl}/catalogs-data/Categories?limit=1000`, { headers })
      .subscribe(
        (data: any) => {
          this.categorias = data.list || data.rows || data || [];
        },
        (error) => {
          console.error('Error al cargar categorías:', error);
        }
      );
  }

  private loadDependencias(): void {
    this.apiService.getCatalogInfo('Departments', 1000, 0).subscribe(
      (data: any) => {
        this.dependencias = data.list || data.rows || [];
      },
      (error) => {
        console.error('Error al cargar dependencias:', error);
      }
    );
  }

  private loadTiposTramite(): void {
    this.tiposTramite = [
      { value: 'Certificación', label: 'Certificación' },
      { value: 'Licencia', label: 'Licencia' },
      { value: 'Permiso', label: 'Permiso' },
      { value: 'Constancia', label: 'Constancia' },
      { value: 'Trámite', label: 'Trámite' },
      { value: 'Registro', label: 'Registro' }
    ];
  }

  onTipoSolicitanteChange(): void {
    const tipoSolicitante = this.form.get('tipoSolicitante')?.value;
    if (tipoSolicitante === 'Física') {
      this.form.patchValue({ personaFisica: true, personaMoral: false });
    } else if (tipoSolicitante === 'Moral') {
      this.form.patchValue({ personaFisica: false, personaMoral: true });
    }
  }

  onEsGratuitoChange(): void {
    const esGratuito = this.form.get('esGratuito')?.value;
    if (esGratuito) {
      this.form.patchValue({ costo: 0 });
    }
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.sendNotification('Error', 'Por favor, completa todos los campos requeridos', 'error');
      return;
    }

    // Validar que al menos una modalidad esté seleccionada
    const modalidadPresencial = this.form.get('modalidadPresencial')?.value;
    const modalidadEnLinea = this.form.get('modalidadEnLinea')?.value;
    const modalidadConCita = this.form.get('modalidadConCita')?.value;

    if (!modalidadPresencial && !modalidadEnLinea && !modalidadConCita) {
      this.sendNotification('Error', 'Debes seleccionar al menos una modalidad de atención', 'error');
      return;
    }

    this.isLoading = true;

    const formData = this.prepareSubmitData();
    const headers = getHeaders();
    
    this.http.post<any>(`${this.baseUrl}/miveracruz/forms`, formData, { headers })
      .subscribe(
        (response) => {
          this.sendNotification('Éxito', 'Trámite creado exitosamente', 'success');
          setTimeout(() => {
            this.router.navigate(['/catalogs/consulta-catalogo']);
          }, 1500);
        },
        (error) => {
          console.error('Error al crear trámite:', error);
          this.sendNotification('Error', 'No se pudo crear el trámite', 'error');
          this.isLoading = false;
        }
      );
  }

  private prepareSubmitData(): any {
    const formValue = this.form.value;
    
    return {
      Common: {
        Name: formValue.nombreTramite,
        Code: formValue.codigoTramite,
        Type: formValue.tipoTramite,
        IsOnline: formValue.modalidadEnLinea,
        IsPresencial: formValue.modalidadPresencial,
        IsConCita: formValue.modalidadConCita,
        Applicant: formValue.tipoSolicitante,
        AnswerTime: formValue.tiempoRespuesta,
        AnswerTimeType: formValue.tipoTiempoRespuesta,
        Validity: formValue.vigencia,
        ValidityType: formValue.tipoVigencia,
        Cost: formValue.esGratuito ? 0 : formValue.costo
      },
      Detail: {
        Description: formValue.descripcion,
        LegalBase: formValue.fundamentoJuridico,
        Office: formValue.nombreOficina,
        Address: formValue.direccionOficina,
        OfficePhone: formValue.telefonoOficina,
        Schedule: formValue.horarioAtencion,
        Requirements: formValue.requisitos,
        Observations: formValue.observaciones,
        IsVisible: formValue.isVisible,
        IsLimited: formValue.isLimited,
        IsRequestable: formValue.isRequestable
      },
      Inquiry: {
        Attender: formValue.nombreContacto,
        Role: formValue.cargoContacto,
        Phone: formValue.telefonoContacto,
        Email: formValue.emailContacto,
        Type: 1
      },
      Form: {
        AreaId: formValue.area,
        CategoryId: formValue.categoria,
        DepartmentId: formValue.dependencia,
        created_by: this.userId || ''
      }
    };
  }

  cancel(): void {
    if (confirm('¿Deseas cancelar? Los cambios no guardados se perderán.')) {
      this.router.navigate(['/catalogs/consulta-catalogo']);
    }
  }

  get formControls() {
    return this.form.controls;
  }

  hasError(fieldName: string): boolean {
    const control = this.form.controls[fieldName];
    return control.invalid && (control.dirty || control.touched || this.submitted);
  }

  getErrorMessage(fieldName: string): string {
    const control = this.form.controls[fieldName];
    if (control.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (control.hasError('minlength')) {
      return `Debe tener al menos ${control.errors.minlength.requiredLength} caracteres`;
    }
    if (control.hasError('email')) {
      return 'Email inválido';
    }
    if (control.hasError('min')) {
      return 'El valor debe ser mayor o igual a 0';
    }
    return '';
  }

  sendNotification(title: string = 'Mi Veracruz', message: string, type: string): void {
    this.toastr[type](message, title, { timeOut: 3500 });
  }
}



